import { Component, Input, OnInit } from '@angular/core';
import { IonNav, NavController } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-dd-consent-form',
  templateUrl: './dd-consent-form.page.html',
  styleUrls: ['./dd-consent-form.page.scss'],
})
export class DdConsentFormPage implements OnInit {

  @Input() caseData: any;
  @Input() debtorDetails: any;
  @Input() planType: any;
  @Input() selectedPlanData: any;
  @Input() isPaymentProposal: any;
  @Input() amount: any;

  consentForm!: FormGroup;
  saveBtnDisabled = false;
  submitted = false;
  showSuccess = false;
  showPendingApproval = false;
  portalSettings: any = {};

  frequencies = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: this.caseService.isClient('simonsagency') ? 'Bi-weekly' : 'Fortnightly' },
    { value: 'four_weekly', label: 'Four Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  constructor(
    private ionNav: IonNav,
    private navCtrl: NavController,
    private caseService: CaseService,
    private commonService: CommonService,
    private fb: FormBuilder,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.portalSettings = JSON.parse(localStorage.getItem('portal_settings') || '{}') || {};
    this.debtorDetails = this.debtorDetails || this.authService.debtorDetails;
    this.initForm();
  }

  initForm() {
    const debtor = this.debtorDetails || {};

    // Split name
    const names = (debtor.debtor_name || '').trim().split(/ (.+)/);
    const firstName = names[0] || '';
    const lastName = names[1] || '';

    // Phone / Mobile number
    let phoneNumber =
      debtor.debtor_mobile != '' && debtor.debtor_mobile != null
        ? debtor.debtor_mobile
        : debtor.debtor_phone != '' && debtor.debtor_phone != null
        ? debtor.debtor_phone
        : '';
    if (!phoneNumber && debtor.phone_numbers?.length > 0) {
      phoneNumber = debtor.phone_numbers[0].number;
    }

    // Email
    let email =
      debtor.debtor_email != '' && debtor.debtor_email != null
        ? debtor.debtor_email
        : '';
    if (!email && debtor.emails?.length > 0) {
      email = debtor.emails[0].account;
    }

    // Address
    // address_ln1 = street line 1, address_ln2 = street line 2, address_ln3 = suburb/city, address_town = state
    const address = debtor.addresses?.[0] || {};
    const address_line1 = address.address_ln1 || '';
    const address_line2 = address.address_ln2 || '';
    const suburb = address.address_ln3 || '';
    const state = address.address_town || '';
    const postcode = address.address_postcode || '';

    // Reference
    const reference = this.caseData?.ref || this.caseData?.cl_ref || this.caseData?.id || this.caseData?.caseid || '';

    // Once Off / Regular Debit prefilling
    let onceOffAmount: number | null = null;
    let onceOffDate = '';
    let regularAmount: number | null = null;
    let regularStarting = '';
    let regularFrequency = 'weekly';

    if (this.planType) {
      const paymentData = this.caseService.paymentData;
      regularAmount = this.parseAmount(paymentData?.amount ?? this.amount ?? null);
      const rawDate: any = paymentData.date;
      regularStarting = rawDate
        ? (typeof rawDate === 'string'
            ? rawDate.split('T')[0]
            : new Date(rawDate).toISOString().split('T')[0])
        : '';
      if (paymentData.freq) {
        const freqStr = String(paymentData.freq).toLowerCase();
        if (freqStr.includes('week') || freqStr === '3') {
          regularFrequency = 'weekly';
        } else if (freqStr.includes('fortnight') || freqStr === '6') {
          regularFrequency = 'fortnightly';
        } else if (freqStr.includes('month') || freqStr === '4' || freqStr === '5') {
          regularFrequency = 'monthly';
        }
      }
    } else {
      const rawAmount = this.amount ?? this.caseService.paymentData?.amount ?? this.caseData?.amount ?? null;
      onceOffAmount = this.parseAmount(rawAmount);
      regularAmount = onceOffAmount;
      onceOffDate = new Date().toISOString().split('T')[0];
    }

    this.consentForm = this.fb.group({
      debtor_type: ['individual', Validators.required], // individual = Person, business = Business
      your_reference: [reference, Validators.required],
      first_name: [firstName, Validators.required],
      last_name: [lastName, Validators.required],
      email: [email, [Validators.required, Validators.email]],
      mobile: [phoneNumber],
      address_line1: [address_line1, Validators.required],
      address_line2: [address_line2],
      suburb: [suburb, Validators.required],
      state: [state, Validators.required],
      postcode: [postcode, Validators.required],

      once_off_amount: [onceOffAmount],
      once_off_date: [onceOffDate],

      regular_amount: [regularAmount],
      regular_starting: [regularStarting],
      regular_frequency: [regularFrequency],
      regular_duration: ['ongoing'],

      payment_method_type: ['Bank Account', Validators.required],
      bank_account_name: [debtor.debtor_name || ''],
      bank_bsb: [''],
      bank_account_number: [''],

      cardholder_name: [debtor.debtor_name || ''],
      card_number: [''],
      card_expiry: [''],
      card_cvv: [''],

      authorization_check: [false, Validators.requiredTrue]
    });

    // Handle dynamic validators for payment method
    this.updatePaymentMethodValidators('Bank Account');
    this.consentForm.get('payment_method_type')?.valueChanges.subscribe(value => {
      this.updatePaymentMethodValidators(value);
    });
  }

  parseAmount(raw: any): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const num = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? null : num;
  }

  updatePaymentMethodValidators(type: string) {
    const bankControls = ['bank_account_name', 'bank_bsb', 'bank_account_number'];
    const cardControls = ['cardholder_name', 'card_number', 'card_expiry', 'card_cvv'];

    if (type === 'Bank Account') {
      bankControls.forEach(ctrlName => {
        this.consentForm.get(ctrlName)?.setValidators([Validators.required]);
        this.consentForm.get(ctrlName)?.updateValueAndValidity({ emitEvent: false });
      });
      cardControls.forEach(ctrlName => {
        this.consentForm.get(ctrlName)?.clearValidators();
        this.consentForm.get(ctrlName)?.updateValueAndValidity({ emitEvent: false });
      });
    } else {
      cardControls.forEach(ctrlName => {
        this.consentForm.get(ctrlName)?.setValidators([Validators.required]);
        this.consentForm.get(ctrlName)?.updateValueAndValidity({ emitEvent: false });
      });
      bankControls.forEach(ctrlName => {
        this.consentForm.get(ctrlName)?.clearValidators();
        this.consentForm.get(ctrlName)?.updateValueAndValidity({ emitEvent: false });
      });
    }
  }

  cancel() {
    this.ionNav.pop();
  }

  submit() {
    if (this.consentForm.invalid) {
      this.submitted = true;
      // Mark all controls as touched to trigger validation UI
      this.consentForm.markAllAsTouched();
      return;
    }
    this.saveBtnDisabled = true;

    // Construct payload
    const formData = {
      ...this.consentForm.value,
      caseid: this.caseData?.id || this.caseData?.caseid
    };

    this.caseService.submitDDConsentForm(formData).subscribe((res: any) => {
      if (res.result) {
        this.showSuccess = true;
        this.showPendingApproval = true;
        setTimeout(() => {
          this.goToPortalHome();
        }, 3000);
      } else {
        this.commonService.showToast(res.message || 'Submission failed. Please try again.', 'danger');
      }
      this.saveBtnDisabled = false;
    });
  }

  proceedToPayment() {
    this.ionNav.pop();
  }

  goToPortalHome() {
    this.ionNav.popToRoot().catch(() => {}).finally(() => {
      this.navCtrl.navigateRoot(['home'], { replaceUrl: true });
    });
  }

}
